import React, { useState, useEffect, useRef, memo } from "react";
import { InfoTip } from "@/components/InfoTip";
import { menuItemCls } from "@/components/ActionBar";
import { ACTION_HELP } from "@/components/actionHelp";
import { useL } from "@/lib/useL";
import { Zap, X, ChevronDown, ChevronUp, CornerDownLeft, Baby, RefreshCcw, Languages, Sparkles } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * CustomInstructionPanel — teacher-authored transformation prompt
 * (branded "Action en 1 clic").
 *
 * UX contract:
 *   - FIRST VISIT (per device): auto-expanded so new users discover the
 *     feature. Dismissing it persists `sux1_ci_dismissed=1` in localStorage
 *     so return visitors land on the collapsed row (no visual noise).
 *   - A "NOUVEAU" badge is shown for 30 days after the first visit
 *     (tracked via `sux1_ci_first_seen` timestamp).
 *   - Auto-expanded whenever an instruction is already filled, or when
 *     `expandOnMount` is true (e.g. prefill from a cas pratique "Tester
 *     dans SUX1" click).
 */

const LS_FIRST_SEEN = "sux1_ci_first_seen";
const LS_DISMISSED  = "sux1_ci_dismissed";
const LS_USED       = "sux1_ci_used";
const NEW_BADGE_DAYS = 30;

// --- Templates for the Pre-Action Bar ---------------------------------------
// Each shortcut pre-fills the custom instruction textarea with a ready-to-run
// prompt. The user can edit it, then press SUMMARIZE. Mirrors the 4 post-
// summary actions (SummaryActions.jsx) so the UX is consistent before/after.
const VULGARISER_SUBS = [
  { key: "enfant",   label: "Enfant (10 ans)",         prompt: "Vulgarise ce texte pour un enfant curieux de 10 ans (CM2). Utilise des mots simples, des phrases courtes, des analogies concrètes. Évite tout jargon." },
  { key: "ado",      label: "Ado (15 ans)",            prompt: "Vulgarise ce texte pour un adolescent de 15 ans (collège/lycée). Vocabulaire quotidien, garde les termes techniques seulement si tu les définis entre parenthèses." },
  { key: "debutant", label: "Débutant complet",        prompt: "Vulgarise ce texte pour un débutant complet sur ce sujet. Définis CHAQUE terme technique à sa première apparition. Pars des bases." },
  { key: "etranger", label: "Non-francophone (B1)",    prompt: "Vulgarise ce texte pour un lecteur non natif de niveau B1. Phrases courtes, vocabulaire courant, évite les idiomes et références culturelles." },
];
const REFORMULER_SUBS = [
  { key: "alt",      label: "Autre tournure",          prompt: "Réécris ce texte avec d'autres mots et structures de phrases en préservant TOUS les faits, chiffres, noms et le sens exact. Objectif : une vraie reformulation (utile pour l'anti-plagiat ou la variété), pas juste un swap de synonymes." },
  { key: "concise",  label: "Plus concis",             prompt: "Réécris ce texte dans un style plus serré et incisif. Coupe le superflu, fusionne les redondances, privilégie les phrases courtes. Préserve TOUS les faits." },
  { key: "formel",   label: "Registre formel",         prompt: "Réécris ce texte dans un registre plus formel et académique. Vocabulaire précis, évite les contractions et familiarités, privilégie l'impersonnel. Garde tous les faits intacts." },
  { key: "simple",   label: "Registre simple",         prompt: "Réécris ce texte dans un registre direct et quotidien. Phrases courtes, mots communs, voix active. Garde tous les faits intacts." },
];
const TRADUIRE_SUBS = [
  { key: "en", label: "English",            prompt: "Traduis ce texte en anglais de manière naturelle et idiomatique (pas littérale). Préserve la structure (titres, puces, paragraphes). Garde les noms propres dans leur forme d'origine." },
  { key: "es", label: "Español",            prompt: "Traduis ce texte en espagnol de manière naturelle et idiomatique. Préserve la structure et les noms propres." },
  { key: "de", label: "Deutsch",            prompt: "Traduis ce texte en allemand de manière naturelle et idiomatique. Préserve la structure et les noms propres." },
  { key: "it", label: "Italiano",           prompt: "Traduis ce texte en italien de manière naturelle et idiomatique. Préserve la structure et les noms propres." },
  { key: "pt", label: "Português",          prompt: "Traduis ce texte en portugais de manière naturelle et idiomatique. Préserve la structure et les noms propres." },
  { key: "nl", label: "Nederlands",         prompt: "Traduis ce texte en néerlandais de manière naturelle et idiomatique. Préserve la structure et les noms propres." },
  { key: "ar", label: "العربية",             prompt: "Traduis ce texte en arabe moderne standard de manière naturelle et idiomatique. Préserve la structure et les noms propres." },
  { key: "zh", label: "中文",                prompt: "Traduis ce texte en chinois simplifié de manière naturelle et idiomatique. Préserve la structure et les noms propres." },
  { key: "ja", label: "日本語",              prompt: "Traduis ce texte en japonais de manière naturelle et idiomatique. Préserve la structure et les noms propres." },
  { key: "ru", label: "Русский",             prompt: "Traduis ce texte en russe de manière naturelle et idiomatique. Préserve la structure et les noms propres." },
  { key: "ko", label: "한국어",               prompt: "Traduis ce texte en coréen de manière naturelle et idiomatique. Préserve la structure et les noms propres." },
  { key: "fr", label: "Français",            prompt: "Traduis ce texte en français de manière naturelle et idiomatique. Préserve la structure et les noms propres." },
];
const POLIR_PROMPT =
  "Corrige l'orthographe, la grammaire, les accents (à, â, é, è, ê, ë, î, ï, ô, ö, ù, û, ç, œ, æ), la ponctuation et la typographie française (espace insécable avant ; : ! ?, guillemets « … »). Fluidifie : lisse les tournures maladroites, répare le rythme, supprime les répétitions. NE change PAS le sens, NE raccourcis PAS, NE rallonge PAS (±10% max).";

const VULGARISER_EN = [
  { key: "enfant", label: "Child (10 years old)", prompt: "Explain this text for a curious 10-year-old. Use simple words, short sentences and concrete analogies. Avoid all jargon." },
  { key: "ado", label: "Teen (15 years old)", prompt: "Explain this text for a 15-year-old. Everyday vocabulary; keep technical terms only if you define them in brackets." },
  { key: "debutant", label: "Complete beginner", prompt: "Explain this text for a complete beginner on the subject. Define EVERY technical term the first time it appears. Start from the basics." },
  { key: "etranger", label: "Non-native reader (B1)", prompt: "Explain this text for a non-native B1-level reader. Short sentences, common vocabulary, avoid idioms and cultural references." },
];
const REFORMULER_EN = [
  { key: "alt", label: "Different wording", prompt: "Rewrite this text with different words and sentence structures while keeping ALL facts, figures, names and the exact meaning. Goal: a real rephrasing, not just synonym swaps." },
  { key: "concise", label: "More concise", prompt: "Rewrite this text in a tighter, sharper style. Cut the superfluous, merge redundancies, prefer short sentences. Keep ALL the facts." },
  { key: "formel", label: "Formal register", prompt: "Rewrite this text in a more formal, academic register. Precise vocabulary, no contractions or casual phrasing. Keep all facts intact." },
  { key: "simple", label: "Simple register", prompt: "Rewrite this text in a direct, everyday register. Short sentences, common words, active voice. Keep all facts intact." },
];
const LANG_EN = { en: "English", es: "Spanish", de: "German", it: "Italian", pt: "Portuguese", nl: "Dutch", ar: "Modern Standard Arabic", zh: "Simplified Chinese", ja: "Japanese", ru: "Russian", ko: "Korean", fr: "French" };
const TRADUIRE_EN = TRADUIRE_SUBS.map((x) => ({ ...x, prompt: `Translate this text into ${LANG_EN[x.key]} naturally and idiomatically (not literally). Keep the structure (headings, bullets, paragraphs) and proper names.` }));
const POLIR_EN = "Correct spelling, grammar, punctuation and typography. Smooth the flow: fix awkward phrasing and rhythm, remove repetitions. Do NOT change the meaning, do NOT shorten or lengthen (±10% max).";

const chipCls = (active) =>
  `font-mono uppercase tracking-[0.14em] text-[10px] px-2.5 py-1.5 border transition-colors flex items-center gap-1.5 ${
    active ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-border hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7]"
  }`;

const readFirstSeen = () => {
  try {
    const raw = localStorage.getItem(LS_FIRST_SEEN);
    if (raw) return parseInt(raw, 10);
    const now = Date.now();
    localStorage.setItem(LS_FIRST_SEEN, String(now));
    return now;
  } catch {
    return Date.now();
  }
};

const readDismissed = () => {
  try { return localStorage.getItem(LS_DISMISSED) === "1"; } catch { return false; }
};
const writeDismissed = (v) => {
  try { localStorage.setItem(LS_DISMISSED, v ? "1" : "0"); } catch {}
};
// Has the user ever interacted with Action en 1 clic (expanded, typed, or
// used a shortcut)? When false, we promote discovery via a prominent
// blue card with a pulse ring.
const readUsed = () => {
  try { return localStorage.getItem(LS_USED) === "1"; } catch { return false; }
};
const markUsed = () => {
  try { localStorage.setItem(LS_USED, "1"); } catch {}
};

function _CustomInstructionPanel({
  value,
  onChange,
  expandOnMount = false,
  disabled = false,
}) {
  // On mount: first-time users see the panel expanded (dismissed=false by
  // default). Returning users who've dismissed it stay collapsed unless
  // they explicitly reopen it or arrive with a prefill.
  const L = useL();
  const VSUBS = L(VULGARISER_SUBS, VULGARISER_EN);
  const RSUBS = L(REFORMULER_SUBS, REFORMULER_EN);
  const TSUBS = L(TRADUIRE_SUBS, TRADUIRE_EN);
  const POLISH = L(POLIR_PROMPT, POLIR_EN);
  const [firstSeen] = useState(() => readFirstSeen());
  const [open, setOpen] = useState(() => expandOnMount || !readDismissed());
  // Pulse ring + prominent card for first-time discovery (fires until the
  // user has either expanded, typed, or used a shortcut once).
  const [neverUsed, setNeverUsed] = useState(() => !readUsed());
  // Flash the panel for 2.5s after a mid-session pre-fill so the user
  // immediately spots the loaded prompt (fixes "dashboard appears empty"
  // bug when arriving from a cas pratique on mobile — panel was below the
  // fold with no visual hook).
  const [flashing, setFlashing] = useState(false);
  const panelRef = useRef(null);

  // Grow the panel back open when a pre-fill arrives mid-session (e.g.
  // the teacher clicked "Tester dans SUX1" on another cas pratique).
  // Also scroll it into view + flash it so it's unmissable on mobile.
  useEffect(() => {
    if (!expandOnMount) return;
    setOpen(true);
    // Defer scroll one frame so the panel is fully rendered.
    const scrollId = requestAnimationFrame(() => {
      try {
        panelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch (_) {}
    });
    setFlashing(true);
    const flashId = setTimeout(() => setFlashing(false), 2500);
    return () => {
      cancelAnimationFrame(scrollId);
      clearTimeout(flashId);
    };
  }, [expandOnMount]);

  const hasInstruction = Boolean((value || "").trim());
  const activeIn = (subs) => subs.find((x) => x.prompt === value)?.key;
  const activeV = activeIn(VSUBS);
  const activeR = activeIn(RSUBS);
  const activeT = activeIn(TSUBS);
  const activeP = value === POLISH;

  // "NEW" badge: show for 30 days after the first visit on this device.
  const showNewBadge = Date.now() - firstSeen < NEW_BADGE_DAYS * 24 * 60 * 60 * 1000;

  const handleCollapse = () => {
    setOpen(false);
    writeDismissed(true);
  };
  const handleExpand = () => {
    setOpen(true);
    writeDismissed(false);
    if (neverUsed) {
      markUsed();
      setNeverUsed(false);
    }
  };
  // Mark the feature as "used" the moment the parent injects any value
  // (shortcut click or typed input). Removes the pulse ring on the next
  // visit even if the user didn't expand the collapsed row manually.
  useEffect(() => {
    if (neverUsed && hasInstruction) {
      markUsed();
      setNeverUsed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInstruction]);

  // Collapsed state: tiny clickable row that announces the feature
  // without hijacking the page. If an instruction is already present,
  // we always expand so the content is never hidden.
  if (!open && !hasInstruction) {
    // Prominent full-width blue bar (pulse ring until first use).
    return (
      <div className="relative mt-4 mb-6 mx-6 md:mx-8 lg:mx-10">
      <button
        type="button"
        data-testid="custom-instruction-expand"
        data-tip={L("Action en 1 clic : au lieu d'un résumé classique, demandez directement un QCM, un mail aux parents, une version DYS, une traduction…", "One-click action: instead of a classic summary, ask directly for a quiz, an email to parents, a DYS version, a translation…")}
        onClick={handleExpand}
        disabled={disabled}
        className={`${neverUsed ? "sux-pulse-ring " : ""}w-full flex items-center justify-center gap-3 bg-[#002FA7] text-white border border-[#002FA7] hover:bg-[#001a66] px-12 py-4 transition-colors`}
      >
        <Zap className="w-5 h-5 shrink-0" />
        <span className="font-mono uppercase tracking-[0.18em] text-[13px] font-semibold">
          {L("Action en 1 clic", "One-click action")}
        </span>
        {(neverUsed || showNewBadge) && (
          <span
            data-testid="custom-instruction-new-badge"
            className="inline-block px-1.5 py-0.5 bg-white text-[#002FA7] font-mono uppercase tracking-[0.15em] text-[9px]"
          >
            {L("Nouveau", "New")}
          </span>
        )}
        <ChevronDown className="w-4 h-4 shrink-0" />
      </button>
      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        <InfoTip light content={ACTION_HELP.oneClick} testid="custom-instruction-expand-info" />
      </div>
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      data-testid="custom-instruction-panel"
      className={`mt-4 mb-6 mx-6 md:mx-8 lg:mx-10 border bg-card transition-colors ${
        hasInstruction ? "border-[#002FA7]" : "border-border"
      } ${flashing ? "ring-4 ring-[#002FA7]/30 shadow-lg" : ""}`}
    >
      <div className="relative flex items-center justify-center px-16 py-3 bg-[#002FA7] text-white">
        <div className="flex items-center gap-2 min-w-0">
          <Zap className="w-4 h-4 shrink-0" />
          <span className="font-mono uppercase tracking-[0.15em] text-[12px] font-semibold truncate">
            {L("Action en 1 clic", "One-click action")} {hasInstruction && L("· active", "· on")}
          </span>
          <InfoTip light content={ACTION_HELP.oneClick} testid="custom-instruction-header-info" />
          {showNewBadge && !hasInstruction && (
            <span
              data-testid="custom-instruction-new-badge"
              className="inline-block px-1.5 py-0.5 bg-white text-[#002FA7] font-mono uppercase tracking-[0.15em] text-[9px]"
            >
              {L("Nouveau", "New")}
            </span>
          )}
        </div>
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2 shrink-0">
          {hasInstruction && (
            <button
              type="button"
              data-testid="custom-instruction-clear"
              onClick={() => onChange("")}
              disabled={disabled}
              className="text-[10px] font-mono uppercase tracking-[0.14em] text-white/80 hover:text-white transition-colors"
              data-tip={L("Effacer l'instruction : revient au résumé classique.", "Clear the instruction: back to the classic summary.")}
            >
              {L("Effacer", "Clear")}
            </button>
          )}
          <button
            type="button"
            data-testid="custom-instruction-collapse"
            onClick={handleCollapse}
            disabled={disabled}
            className="text-white/80 hover:text-white"
            data-tip={L("Replier ce panneau.", "Collapse this panel.")}
          >
            {hasInstruction ? <X className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div className="p-4">
        {/* Pre-Action Bar — 1-click shortcuts that pre-fill the textarea */}
        <div
          data-testid="pre-action-bar"
          className="mb-3 flex flex-wrap items-center gap-2"
        >
          <span className="font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground mr-1">
            {L("Raccourcis :", "Shortcuts:")}
          </span>

          {/* Vulgariser */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="pre-action-vulgariser"
                disabled={disabled}
                className={chipCls(activeV)}
                title={L("Pré-remplir : vulgariser pour un public précis", "Pre-fill: simplify for a specific audience")}
              >
                <Baby className="w-3 h-3" /> {L("Vulgariser", "Simplify")}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {VSUBS.map((s) => (
                <DropdownMenuItem
                  key={s.key}
                  data-testid={`pre-action-vulgariser-${s.key}`}
                  onSelect={() => onChange(s.prompt)}
                  className={menuItemCls(activeV === s.key)}
                >
                  {s.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <InfoTip testid="pre-action-vulgariser-info" content={L(<><b>Vulgariser</b> : rend le texte compréhensible pour un public précis. Choisissez : enfant de 10 ans, ado de 15 ans, débutant complet ou lecteur non francophone (niveau B1). Les mots techniques sont expliqués ou remplacés.</>, <><b>Simplify</b>: makes the text understandable for a specific audience. Choose: 10-year-old child, 15-year-old teen, complete beginner or non-native reader (B1 level). Technical words are explained or replaced.</>)} />

          {/* Reformuler */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="pre-action-reformuler"
                disabled={disabled}
                className={chipCls(activeR)}
                title={L("Pré-remplir : reformuler dans un autre style", "Pre-fill: rephrase in another style")}
              >
                <RefreshCcw className="w-3 h-3" /> {L("Reformuler", "Rephrase")}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {RSUBS.map((s) => (
                <DropdownMenuItem
                  key={s.key}
                  data-testid={`pre-action-reformuler-${s.key}`}
                  onSelect={() => onChange(s.prompt)}
                  className={menuItemCls(activeR === s.key)}
                >
                  {s.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <InfoTip testid="pre-action-reformuler-info" content={L(<><b>Reformuler</b> : réécrit le texte avec d'autres mots, sans changer le sens ni les faits. Choisissez : autre tournure, plus concis, registre formel ou registre simple.</>, <><b>Rephrase</b>: rewrites the text with other words, without changing the meaning or facts. Choose: different wording, more concise, formal or simple register.</>)} />

          {/* Traduire */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="pre-action-traduire"
                disabled={disabled}
                className={chipCls(activeT)}
                title={L("Pré-remplir : traduire en 12 langues", "Pre-fill: translate into 12 languages")}
              >
                <Languages className="w-3 h-3" /> {L("Traduire", "Translate")}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-[320px] overflow-y-auto">
              {TSUBS.map((s) => (
                <DropdownMenuItem
                  key={s.key}
                  data-testid={`pre-action-traduire-${s.key}`}
                  onSelect={() => onChange(s.prompt)}
                  className={menuItemCls(activeT === s.key)}
                >
                  {s.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <InfoTip testid="pre-action-traduire-info" content={L(<><b>Traduire</b> : traduit le texte de façon naturelle (pas mot à mot) dans l'une des 12 langues proposées, en gardant les titres et les listes.</>, <><b>Translate</b>: translates the text naturally (not word for word) into one of the 12 languages offered, keeping headings and lists.</>)} />

          {/* Polir — direct action, no submenu */}
          <button
            type="button"
            data-testid="pre-action-polir"
            disabled={disabled}
            onClick={() => onChange(POLISH)}
            className={chipCls(activeP)}
            title={L("Pré-remplir : corriger orthographe + fluidifier", "Pre-fill: fix spelling + smooth the flow")}
          >
            <Sparkles className="w-3 h-3" /> {L("Polir", "Polish")}
          </button>
          <InfoTip testid="pre-action-polir-info" content={L(<><b>Polir</b> : corrige l'orthographe, la grammaire et la ponctuation, et rend les phrases plus fluides, sans changer le fond. Action directe, sans menu.</>, <><b>Polish</b>: fixes spelling, grammar and punctuation, and makes sentences flow better, without changing the content. Direct action, no menu.</>)} />
        </div>

        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={L('Ex : "Génère 10 QCM à 4 choix avec corrigé en gras, de la plus simple à la plus complexe"', 'E.g. "Generate 10 multiple-choice questions with 4 options, answers in bold, from easiest to hardest"')}
          rows={3}
          data-testid="custom-instruction-input"
          disabled={disabled}
          maxLength={2000}
          className="w-full bg-background border border-border p-3 font-sans text-sm resize-y focus:outline-none focus:border-[#002FA7] disabled:opacity-60"
        />
        {hasInstruction ? (
          <div
            data-testid="custom-instruction-active-note"
            className="mt-3 flex items-start gap-2 text-[11px] text-[#1A7A3E] bg-[#1A7A3E]/10 border border-[#1A7A3E]/30 p-2"
          >
            <CornerDownLeft className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              {L(<><b>Mode action directe</b> · Votre instruction remplace le format classique. Elle est appliquée au contenu en 1 clic (coût d'un résumé). Le choix du format est désactivé tant que ce champ est rempli ; les autres réglages (modèle, longueur, langue, registre) restent actifs.</>,
                 <><b>Direct action mode</b> · Your instruction replaces the classic format. It is applied to the content in 1 click (costs like a summary). The format choice is disabled while this field is filled; the other settings (model, length, language, register) stay active.</>)}
            </span>
          </div>
        ) : (
          <div className="mt-2 text-[11px] text-muted-foreground">
            {L("Appliquez une transformation directe à votre contenu en 1 clic (QCM, mail aux parents, reformulation DYS, plan de séance…) au lieu de faire un résumé puis une question de suivi. Si vous laissez vide, SUX1 fait un résumé classique selon le format choisi.",
               "Apply a direct transformation to your content in 1 click (quiz, email to parents, DYS rephrasing, lesson plan…) instead of a summary followed by a follow-up question. If left empty, SUX1 makes a classic summary in the chosen format.")}
          </div>
        )}
      </div>
    </div>
  );
}

// Memoised to avoid re-renders during streaming — Dashboard pushes state
// updates every ~120 ms during a live preview and this component's output
// doesn't depend on any of those updates, only on `value` / `expandOnMount` /
// `disabled`.
export const CustomInstructionPanel = memo(_CustomInstructionPanel);
